import type { WsAck, WsClientMessage, WsEventEnvelope } from '@/lib/api/types';
import { WS_BACKOFF_MAX_MS, WS_HIDDEN_CLOSE_MS, WS_IDLE_TIMEOUT_MS, WS_PING_INTERVAL_MS, WS_UNSUB_GRACE_MS } from '@/config/constants';

export type Unsubscribe = () => void;
export type EventHandler = (env: WsEventEnvelope) => void;
export type ConnectionState = 'idle' | 'connecting' | 'open' | 'closed';

export type RealtimeOptions = {
  /** Returns the socket URL (incl. `?token=`); called on every (re)connect so tokens stay fresh. */
  getUrl: () => Promise<string>;
  /** Injectable for tests. */
  WebSocketImpl?: typeof WebSocket;
  storage?: Pick<Storage, 'getItem' | 'setItem'> | null;
  onReplayGap?: (room: string) => void;
  onState?: (s: ConnectionState) => void;
  now?: () => number;
};

function cmpIds(a: string, b: string): number {
  try {
    const x = BigInt(a);
    const y = BigInt(b);
    return x < y ? -1 : x > y ? 1 : 0;
  } catch {
    return a < b ? -1 : a > b ? 1 : 0;
  }
}

/** §4.6 — one socket per tab, ref-counted rooms, last_event_id replay, backoff, idle ping. */
export class RealtimeClient {
  private ws: WebSocket | null = null;
  private refs = new Map<string, number>();
  private graceTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private subscribed = new Set<string>();
  private lastIds = new Map<string, string>();
  private handlers = new Map<string, Set<EventHandler>>();
  private attempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private idleTimer: ReturnType<typeof setTimeout> | null = null;
  private hiddenTimer: ReturnType<typeof setTimeout> | null = null;
  private lastMessageAt = 0;
  private closedByUser = false;
  private reqSeq = 0;
  state: ConnectionState = 'idle';
  private stateListeners = new Set<(s: ConnectionState) => void>();
  private readonly storage: Pick<Storage, 'getItem' | 'setItem'> | null;

  constructor(private opts: RealtimeOptions) {
    this.storage = opts.storage === undefined ? (typeof sessionStorage !== 'undefined' ? sessionStorage : null) : opts.storage;
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', this.onVisibility);
  }

  private readonly onVisibility = (): void => {
    if (document.hidden) {
      if (!this.hiddenTimer) this.hiddenTimer = setTimeout(() => this.ws?.close(4000, 'hidden'), WS_HIDDEN_CLOSE_MS);
    } else {
      if (this.hiddenTimer) clearTimeout(this.hiddenTimer);
      this.hiddenTimer = null;
      // the hidden close (and any close that happened while hidden) did not schedule a reconnect
      if (!this.ws && !this.closedByUser && this.refs.size > 0) this.connect();
    }
  };

  private setState(s: ConnectionState) {
    this.state = s;
    this.opts.onState?.(s);
    for (const fn of this.stateListeners) fn(s);
  }

  /** Component-scoped connection state listener (e.g. refetch after a reconnect). */
  onConnectionState(fn: (s: ConnectionState) => void): Unsubscribe {
    this.stateListeners.add(fn);
    return () => {
      this.stateListeners.delete(fn);
    };
  }

  connect(): void {
    if (this.ws || this.reconnectTimer) return;
    this.closedByUser = false;
    this.setState('connecting');
    void this.opts
      .getUrl()
      .then((url) => {
        if (this.closedByUser) return;
        const Impl = this.opts.WebSocketImpl ?? WebSocket;
        const ws = new Impl(url);
        this.ws = ws;
        ws.onopen = () => {
          this.lastMessageAt = this.now();
          this.setState('open');
          this.subscribed.clear();
          const rooms = [...this.refs.keys()];
          if (rooms.length) this.sendSubscribe(rooms);
          this.startTimers();
        };
        ws.onmessage = (ev) => this.onMessage(String(ev.data));
        ws.onclose = () => this.onClose();
        ws.onerror = () => {
          /* onclose follows */
        };
      })
      .catch(() => this.onClose());
  }

  close(): void {
    this.closedByUser = true;
    if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', this.onVisibility); // L8
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    if (this.hiddenTimer) clearTimeout(this.hiddenTimer);
    this.hiddenTimer = null;
    this.stopTimers();
    this.ws?.close(1000, 'bye');
    this.ws = null;
    this.setState('closed');
  }

  private onClose(): void {
    this.stopTimers();
    this.ws = null;
    this.subscribed.clear();
    if (this.closedByUser) return;
    this.setState('closed');
    if (this.refs.size === 0) return;
    // power saving: while the tab is hidden stay closed; visibilitychange reconnects (REVIEW 28)
    if (typeof document !== 'undefined' && document.hidden) return;
    const delay = Math.min(WS_BACKOFF_MAX_MS, 1000 * 2 ** this.attempt) + Math.random() * 500;
    this.attempt++;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  private now(): number {
    return this.opts.now ? this.opts.now() : Date.now();
  }

  private startTimers(): void {
    this.stopTimers();
    this.pingTimer = setInterval(() => {
      if (this.now() - this.lastMessageAt > WS_IDLE_TIMEOUT_MS) {
        this.ws?.close(4001, 'idle');
        return;
      }
      if (this.now() - this.lastMessageAt >= WS_PING_INTERVAL_MS - 50) this.send({ op: 'ping' });
    }, WS_PING_INTERVAL_MS);
  }
  private stopTimers(): void {
    if (this.pingTimer) clearInterval(this.pingTimer);
    if (this.idleTimer) clearTimeout(this.idleTimer);
    this.pingTimer = null;
    this.idleTimer = null;
  }

  private send(msg: WsClientMessage): boolean {
    if (!this.ws || this.ws.readyState !== 1) return false;
    this.ws.send(JSON.stringify(msg));
    return true;
  }

  private sendSubscribe(rooms: string[]): void {
    // one subscribe per distinct last_event_id so replay offsets stay per-room
    const groups = new Map<string | null, string[]>();
    for (const r of rooms) {
      const last = this.lastId(r);
      const g = groups.get(last) ?? [];
      g.push(r);
      groups.set(last, g);
    }
    for (const [last, rs] of groups) {
      if (this.send({ op: 'subscribe', rooms: rs, last_event_id: last, req_id: `s${++this.reqSeq}` })) for (const r of rs) this.subscribed.add(r);
    }
  }

  subscribe(room: string): Unsubscribe {
    const t = this.graceTimers.get(room);
    if (t) {
      clearTimeout(t);
      this.graceTimers.delete(room);
    }
    const n = (this.refs.get(room) ?? 0) + 1;
    this.refs.set(room, n);
    if (n === 1 && !this.subscribed.has(room)) {
      if (this.ws?.readyState === 1) this.sendSubscribe([room]);
      else this.connect();
    }
    let done = false;
    return () => {
      if (done) return;
      done = true;
      const c = (this.refs.get(room) ?? 1) - 1;
      if (c > 0) {
        this.refs.set(room, c);
        return;
      }
      this.refs.set(room, 0);
      this.graceTimers.set(
        room,
        setTimeout(() => {
          this.graceTimers.delete(room);
          if ((this.refs.get(room) ?? 0) > 0) return;
          this.refs.delete(room);
          if (this.subscribed.has(room)) {
            this.send({ op: 'unsubscribe', rooms: [room], req_id: `u${++this.reqSeq}` });
            this.subscribed.delete(room);
          }
        }, WS_UNSUB_GRACE_MS),
      );
    };
  }

  presence(room: string, playhead_ms?: number | null, version_id?: string | null): void {
    this.send({ op: 'presence.heartbeat', presence: { room, playhead_ms: playhead_ms ?? null, version_id: version_id ?? null } });
  }

  on(type: string | '*', handler: EventHandler): Unsubscribe {
    const set = this.handlers.get(type) ?? new Set();
    set.add(handler);
    this.handlers.set(type, set);
    return () => {
      set.delete(handler);
    };
  }

  rooms(): string[] {
    return [...this.refs.keys()];
  }

  lastId(room: string): string | null {
    const mem = this.lastIds.get(room);
    if (mem) return mem;
    const v = this.storage?.getItem(`ws:last:${room}`) ?? null;
    if (v) this.lastIds.set(room, v);
    return v;
  }
  private setLastId(room: string, id: string): void {
    this.lastIds.set(room, id);
    try {
      this.storage?.setItem(`ws:last:${room}`, id);
    } catch {
      /* quota */
    }
  }

  /** Exposed for tests: feed a raw server frame. */
  onMessage(raw: string): void {
    this.lastMessageAt = this.now();
    let msg: WsAck | WsEventEnvelope;
    try {
      msg = JSON.parse(raw) as WsAck | WsEventEnvelope;
    } catch {
      return;
    }
    if ('op' in msg) {
      if (msg.op === 'ack' && msg.ok) {
        this.attempt = 0;
        if (msg.replay_truncated) for (const r of msg.rooms ?? []) this.opts.onReplayGap?.(r);
      } else if (msg.op === 'error') {
        for (const r of msg.rooms ?? []) this.opts.onReplayGap?.(r);
      }
      return;
    }
    const env = msg;
    if (!env.id || !env.room) return;
    const last = this.lastId(env.room);
    if (last && cmpIds(env.id, last) <= 0) return; // duplicate/replayed-behind
    this.setLastId(env.room, env.id);
    for (const h of this.handlers.get(env.type) ?? []) h(env);
    for (const h of this.handlers.get('*') ?? []) h(env);
  }
}
